import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";
import {FormGroup} from "@angular/forms";
import {ErrorHandlerService} from "../../../error-handler.service";
import {CustomerInstanceVenueModel} from "../customer-instance-venue.model";
import {SignupStatusData} from "../../signup/signup-status.data";
import {SignupService} from "../../signup/signup.service";
import {ContractService, SignerResponse} from "../contract/contract.service";
import {FUIAlertAction, FUIAlertActions, FUIAlertService, FUISpinnersService} from "@fedelta-pos/ui";
import {UserData} from "../../auth/user.data";
import {CustomerInstanceVenueData} from "../customer-instance-venue.data";
import {CustomerInstanceVenueService} from "../customer-instance-venue.service";
import {Subscription} from "rxjs";

@Component({
    selector: "fhm-signup-status",
    styles: [":host {display: flex; width: 100%;}"],
    templateUrl: "./signup-status.component.html",
    styleUrls: ["./signup-status.component.scss"],
    providers: [SignupService, ContractService],
    standalone: false
})
export class SignupStatusComponent implements OnInit, OnDestroy {
    private static readonly MAX_UPLOAD_CONTRACT_SIZE = 10485760;

    UserData = UserData;

    data: SignupStatusData;

    subscription: Subscription = new Subscription();

    @Input() formGroupContact: FormGroup;
    @Input() loggedUser: UserData;

    @Output() sendSignUpUrlClicked: EventEmitter<any> = new EventEmitter();
    @Output() signInPersonClicked: EventEmitter<any> = new EventEmitter();
    @Output() openCustomerPortalClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() openAppPaymentAccountClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() getSignUpUrlClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() getSetupAppPaymentAccountUrlClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() discontinueClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() recontinueClicked: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild("inputUploadFile", {read: ElementRef}) public inputUploadFile: ElementRef;

    constructor(
                private errorHandler: ErrorHandlerService,
                private spinnersService: FUISpinnersService,
                private contractService: ContractService,
                private alertService: FUIAlertService,
                private customerInstanceVenueService: CustomerInstanceVenueService,
                public model: CustomerInstanceVenueModel) {
    }

    ngOnInit(): void {

    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    get customContract(): boolean {
        return this.model.data.contract && this.model.data.contract.plan
            && this.model.data.contract.plan.name && (this.model.data.contract.plan.name.indexOf("CS") !== -1 || this.model.data.contract.plan.name.indexOf("TYRO") !== -1);
    }

    get signUpSent(): boolean {
        return !!this.model.data.venue.signUpUrlSentDate;
    }

    get validContractFileTypes(): string {
        return ".pdf";
    }

    uploadContract(): void {
        this.inputUploadFile.nativeElement.click();
    }

    inputUploadContractClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    fileSelected(event: Event): void {
        const fileList: FileList = (<HTMLInputElement>event.target).files;
        if (fileList && fileList.length > 0) {
            try {
                const file: File = fileList[0];
                if (file.type !== "application/pdf") {
                    return;
                }
                if (file.size > SignupStatusComponent.MAX_UPLOAD_CONTRACT_SIZE) {
                    this.alertService.showError("fhm.contract.alert.uploadContractSizeLimit.title",
                        "fhm.contract.alert.uploadContractSizeLimit.message").subscribe(() => {
                    });
                    return;
                }
                this.uploadContractFile(file);
            } finally {
                (<HTMLInputElement>event.target).value = null;
            }
        }
    }

    async uploadContractFile(file): Promise<void> {
        this.spinnersService.open();
        try {
            await this.contractService.uploadContract(this.model.venueId, file);
            await this.model.refresh();
            this.spinnersService.close();
        } catch (e) {
            this.spinnersService.close();
            this.errorHandler.handleErrorResponse(e);
        }
    }

    signInPerson(): void {
        this.signInPersonClicked.emit();
    }

    sendSignUpUrl(): void {
        this.sendSignUpUrlClicked.emit();
    }

    async viewDocAsDirector(): Promise<void> {
        this.spinnersService.open();
        const windowReference: Window = window.open();
        try {
            const resp: SignerResponse = await this.contractService.getDirectorSignerLink(this.model.venueId);
            this.spinnersService.close();
            windowReference.location.href = resp.url;
        } catch (e) {
            this.spinnersService.close();
            windowReference.close();
            this.errorHandler.handleErrorResponse(e);
        }
    }

    async downloadContract(): Promise<void> {
        this.spinnersService.open();
        try {
            await this.contractService.downloadContract(this.model.venueId);
            this.spinnersService.close();
        } catch (e) {
            this.spinnersService.close();
            this.errorHandler.handleErrorResponse(e);
        }
    }

    async resetContract(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.contract.alert.reset.title",
            "fhm.contract.alert.reset.message").toPromise();

        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                await this.contractService.resetContract(this.model.venueId);
                await this.model.refresh();
                this.spinnersService.close();
            } catch (e) {
                this.spinnersService.close();
                this.errorHandler.handleErrorResponse(e);
            }
        }
    }

    get showMarkAsReady(): boolean {
        return !UserData.isSalesManager(this.loggedUser) && !UserData.isSalesRep(this.loggedUser)
            && this.model.data.venue.deploymentStatus === CustomerInstanceVenueData.DEPLOYMENT_STATUS_PENDING;
    }

    async markAsReady(): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showWarningOkCancel("fhm.customer-instance-venue.alert.markReady.title",
            "fhm.customer-instance-venue.alert.markReady.message").toPromise();

        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open();
            try {
                this.model.data.venue = await this.customerInstanceVenueService.markReadyDeployment(this.model.venueId);
                this.spinnersService.close();
            } catch (e) {
                this.spinnersService.close();
                this.errorHandler.handleErrorResponse(e);
            }
        }
    }

    get tyro(): boolean {
        return this.model.data && this.model.data.venuePlan && this.model.data.venuePlan.planName && this.model.data.venuePlan.planName.indexOf("TYRO") !== -1;
    }

    get showOpenCustomerStripe(): boolean {
        return !UserData.isSalesManager(this.loggedUser) && !UserData.isSalesRep(this.loggedUser) && this.model.data.billingAccount
            && !!this.model.data.billingAccount.integrationCustomerId && !this.tyro;
    }

    openCustomerStripe(): void {
        window.open("https://dashboard.stripe.com/customers/" + this.model.data.billingAccount.integrationCustomerId, "_blank");
    }

    openCustomerPortal(): void {
        this.openCustomerPortalClicked.emit();
    }

    openAppPaymentAccountSetup(): void {
        this.openAppPaymentAccountClicked.emit();
    }

    getSignUpUrl(): void {
        this.getSignUpUrlClicked.emit();
    }

    getSetupAppPaymentAccountUrl(): void {
        this.getSetupAppPaymentAccountUrlClicked.emit();
    }

    discontinue(): void {
        this.discontinueClicked.emit();
    }

    recontinue(): void {
        this.recontinueClicked.emit();
    }

    protected readonly CustomerInstanceVenueData = CustomerInstanceVenueData;
}
